import { useCallback, useMemo, useRef, useState } from 'react'
import { classifyCaptureFailure, newCaptureId } from '../lib/capture-flow'
import type { CaptureFailureKind } from '../lib/capture-flow'

export interface CaptureCommand {
  sku: string
  qty: number
  mode: 'add' | 'set'
}

export interface CaptureRequest extends CaptureCommand {
  captureId: string
}

export interface CaptureSubmission {
  /** A capture is on the wire: the confirm control is blocked, so a slow request cannot double. */
  pending: boolean
  failure: CaptureFailureKind | null
  /**
   * Sends a new capture under a fresh `captureId`. Resolves true when the server accepted it;
   * false when it failed (the attempt is kept for `resend`) or when one was already in flight.
   */
  submit: (command: CaptureCommand) => Promise<boolean>
  /** Repeats the failed attempt under the SAME `captureId`, so it cannot count twice. */
  resend: () => Promise<boolean>
  /** Forgets the attempt and clears the failure — the sheet was closed or a new target opened. */
  reset: () => void
}

/**
 * Owns the lifecycle of one capture attempt.
 *
 * The `captureId` is minted when the sheet submits and reused for every resend of that same
 * submission, which is what makes a retry after a timeout safe: the server records accepted ids
 * and answers a repeat with the current item state instead of adding the quantity again. Typing a
 * different number and confirming again is a new capture and gets a new id.
 *
 * At most one capture is ever in flight — the guard is a ref, not the `pending` state, so two taps
 * inside one render cannot both get through.
 *
 * `send` and `makeId` are read through refs, so the returned callbacks stay stable across renders
 * even though react-query hands out a new mutation object every time.
 */
export function useCaptureSubmission(
  send: (request: CaptureRequest) => Promise<unknown>,
  makeId: () => string = newCaptureId,
): CaptureSubmission {
  const sendRef = useRef(send)
  sendRef.current = send
  const makeIdRef = useRef(makeId)
  makeIdRef.current = makeId

  const inFlightRef = useRef(false)
  const attemptRef = useRef<CaptureRequest | null>(null)
  const [pending, setPending] = useState(false)
  const [failure, setFailure] = useState<CaptureFailureKind | null>(null)

  const run = useCallback(async (request: CaptureRequest) => {
    if (inFlightRef.current) return false
    inFlightRef.current = true
    attemptRef.current = request
    setPending(true)
    setFailure(null)
    try {
      await sendRef.current(request)
      attemptRef.current = null
      return true
    } catch (error) {
      // The attempt is deliberately kept: the sheet stays open with the typed quantity, and the
      // resend has to reuse this id rather than mint a new one.
      setFailure(classifyCaptureFailure(error))
      return false
    } finally {
      inFlightRef.current = false
      setPending(false)
    }
  }, [])

  const submit = useCallback(
    (command: CaptureCommand) =>
      run({ ...command, captureId: makeIdRef.current() }),
    [run],
  )

  const resend = useCallback(() => {
    const attempt = attemptRef.current
    if (!attempt) return Promise.resolve(false)
    return run(attempt)
  }, [run])

  const reset = useCallback(() => {
    attemptRef.current = null
    setFailure(null)
  }, [])

  // Memoized so callbacks built on top of it (and with them the scanner's `onHit`) stay stable.
  return useMemo(
    () => ({ pending, failure, submit, resend, reset }),
    [pending, failure, submit, resend, reset],
  )
}
